import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, X, Menu } from 'lucide-react';
import '../styles/Sidebar.css';
import ProductLog from '../images/logo.png';
import { APPLICATION_SHORT_NAME } from '../constants/AppConfig';
import { useCompany } from '../context/CompanyContext';

export interface MenuItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
  divider?: boolean;      // when true renders a visual separator — no navigation
  children?: MenuItem[];
  apps?: string[];        // top-level only: app codes (AppSwitcher) this item belongs to; omit to show under every app
}

interface SidebarProps {
  navigationItems: MenuItem[];
  isExpanded: boolean;
  onToggle: (expanded: boolean) => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns true when pathname matches item.path exactly or starts with path+'/'. */
const isDescendantActive = (items: MenuItem[], pathname: string): boolean =>
  items.some((item) => {
    if (item.divider) return false;
    if (pathname === item.path || pathname.startsWith(item.path + '/')) return true;
    if (item.children) return isDescendantActive(item.children, pathname);
    return false;
  });

/** Recursively marks every ancestor of the active path as expanded. */
const buildAutoExpand = (
  items: MenuItem[],
  pathname: string,
  result: Record<string, boolean>,
): void => {
  items.forEach((item) => {
    if (item.divider || !item.children) return;
    const realChildren = item.children.filter((c) => !c.divider);
    if (isDescendantActive(realChildren, pathname)) {
      result[item.path] = true;
    }
    buildAutoExpand(realChildren, pathname, result);
  });
};

// ── Component ─────────────────────────────────────────────────────────────────

const Sidebar: React.FC<SidebarProps> = ({ navigationItems, isExpanded, onToggle }) => {
  const location = useLocation();
  const { logoUrl } = useCompany();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);

  // Only present in the Electron desktop build — a plain web build has no
  // installer version to show, so the footer stays hidden there.
  useEffect(() => {
    window.electronAPI?.getAppVersion().then(setAppVersion);
  }, []);

  // Auto-expand every ancestor of the current route
  useEffect(() => {
    const autoExpand: Record<string, boolean> = {};
    buildAutoExpand(navigationItems, location.pathname, autoExpand);
    setExpandedMenus((prev) => ({ ...prev, ...autoExpand }));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleMenuToggle = (path: string) => {
    setExpandedMenus((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const isPathActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  /**
   * Renders a single menu item (or a divider).
   * depth  — nesting level (0 = root)
   * index  — sibling position, used to build a unique React key
   */
  const renderMenuItem = (item: MenuItem, depth = 0, index = 0): React.ReactNode => {
    // ── Divider ──────────────────────────────────────────────────────────────
    if (item.divider) {
      return (
        <div
          key={`${item.path}-${depth}-${index}`}
          className="nav-divider"
          role="separator"
          aria-hidden="true"
        />
      );
    }

    const itemKey     = `${item.path}-${depth}-${index}`;
    const itemExpanded = expandedMenus[item.path];
    const realChildren = (item.children ?? []).filter((c) => !c.divider);
    const hasChildren  = realChildren.length > 0;
    const active       = isPathActive(item.path);

    return (
      <div key={itemKey} className="nav-item-wrapper">
        <div className={`nav-item ${depth > 0 ? 'nav-item-child' : 'nav-item-root'}`}>
          {hasChildren ? (
            <button
              type="button"
              className={`nav-link nav-link-expandable${itemExpanded ? ' expanded' : ''}${active ? ' active' : ''}`}
              onClick={() => handleMenuToggle(item.path)}
              title={item.label}
              aria-expanded={itemExpanded}
              aria-label={`${item.label} submenu`}
            >
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              <span className={`nav-chevron${itemExpanded ? ' rotated' : ''}`} aria-hidden="true">
                <ChevronDown size={13} strokeWidth={2.5} />
              </span>
            </button>
          ) : (
            <Link
              to={item.path}
              className={`nav-link${active ? ' active' : ''}`}
              title={item.label}
              aria-label={item.label}
            >
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          )}
        </div>

        {hasChildren && itemExpanded && (
          <div className="nav-submenu" role="list">
            {item.children!.map((child, idx) => renderMenuItem(child, depth + 1, idx))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* ── Mobile hamburger button ── */}
      <button
        className="sidebar-hamburger"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={mobileOpen}
        aria-controls="main-sidebar"
      >
        {mobileOpen ? <X size={20} strokeWidth={2.5} /> : <Menu size={20} strokeWidth={2.5} />}
      </button>

      {/* ── Mobile overlay ── */}
      <div
        className={`sidebar-overlay${mobileOpen ? ' visible' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* ── Sidebar ── */}
      <nav
        id="main-sidebar"
        className={`sidebar${isExpanded ? ' expanded' : ' collapsed'}${mobileOpen ? ' mobile-open' : ''}`}
        aria-label="Main navigation"
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="logo-section">
            {/* Company logo (from settings) with fallback to product default */}
            <img
              src={logoUrl || ProductLog}
              alt={APPLICATION_SHORT_NAME}
              className="layout-header-logo"
            />
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => onToggle(!isExpanded)}
            title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-expanded={isExpanded}
          >
            {isExpanded
              ? <ChevronLeft size={16} strokeWidth={2.5} aria-hidden={true} />
              : <ChevronRight size={16} strokeWidth={2.5} aria-hidden={true} />}
          </button>
        </div>

        {/* Scrollable menu area */}
        <div className="sidebar-body">
          <ul className="sidebar-menu">
            {navigationItems.map((item, idx) => renderMenuItem(item, 0, idx))}
          </ul>
        </div>

        {appVersion && (
          <div className="sidebar-footer">
            {isExpanded ? `v${appVersion}` : `v${appVersion.split('.')[0]}`}
          </div>
        )}
      </nav>
    </>
  );
};

export default Sidebar;
